import { validateTimeFields } from '../../../../common/timeFormat';
/* eslint react/prop-types: 0 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { useFormik } from 'formik';
import { validatePercentageFields } from '../../../../common/percentageFormat';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import { useFormData } from '../../../hooks/use-form-data';
import { updateValues } from '../../../services/api/update-fields.api';

import FullDetailsAccordion from '../../../components/FullDetailsAccordion';
import DataView from '../../../components/DataView';
import AssetImageCarousel from '../../../../components/AssetImageCarousel';
import { AUTOCLOSE_ALERT_DELAY } from '../../../shared/constants/snackbar.constants';
import { ENTITY_OBJECT_TYPES } from '../../../shared/constants/entity.constants';

import ApiService from '../../../../common/ApiCalls';

import './styles.scss';

/**
 * @param {Array<FieldsData>} cards
 * @returns {Record<string, boolean>}
 */
const initAccordions = (cards) =>
  cards.reduce((acc, curr, idx) => {
    const key = `${curr.title}-${idx}`;
    acc[key] = true;

    return acc;
  }, {});

/**
 * @param {{ fieldsData: Array<import("../../../hooks/use-form-data").FieldsData> }} props
 */
const LocationTab = ({ fieldsData, showLoader, addressCoords }) => {
  const defaultAlertData = { type: '', message: '' };
  const { id: tabId } = useParams();
  const [showAlert, setShowAlert] = useState(false);
  const [alertData, setAlertData] = useState(defaultAlertData);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState({});

  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [imagesToAdd, setImagesToAdd] = useState([]);

  const reloadImagesRef = useRef(null);

  const storeReloadFunction = useCallback((reloadFn) => {
    reloadImagesRef.current = reloadFn;
  }, []);

  const { formData, setFormData } = useFormData(fieldsData);

  useEffect(() => {
    setSelectedId(initAccordions(fieldsData));
  }, [fieldsData]);

  const handleImageChange = (change) => {
    switch (change.type) {
      case 'add':
        return setImagesToAdd((prev) => [...prev, change.image]);
      case 'delete':
        return setImagesToDelete((prev) => [...prev, change.imageId]);
      case 'remove-pending':
        return setImagesToAdd((prev) =>
          prev.filter((img) => img.tempId !== change.tempId)
        );
      default:
        return;
    }
  };

  const resetPendingImages = () => {
    imagesToAdd.forEach((img) => {
      if (img.type === 'file') URL.revokeObjectURL(img.url);
    });
    setImagesToAdd([]);
    setImagesToDelete([]);
  };

  const discardChanges = () => {
    setIsEditMode(false);
    resetPendingImages();
  };

  const deleteImages = async () => {
    for (const imageId of imagesToDelete) {
      try {
        await ApiService().deleteImage(imageId);
      } catch (e) {
        setShowAlert(true);
        setAlertData({
          type: 'error',
          message: 'Failed to delete image.'
        });
      }
    }
  };

  const updateImages = async () => {
    for (const image of imagesToAdd) {
      try {
        if (image.type === 'url') {
          await ApiService().createImage(tabId, { imageUrl: image.url });
        }
        if (image.type === 'file') {
          await ApiService().updateLocationImage([image.file], tabId, 'tab0');
          URL.revokeObjectURL(image.url);
        }
      } catch (e) {
        setShowAlert(true);
        setAlertData({
          type: 'error',
          message: 'Failed to update image.'
        });
      }
    }
  };

  const saveChanges = async (values) => {
    const payload = { values: [] };

    for (const fieldId of Object.keys(values)) {
      payload.values.push({
        field_id: fieldId,
        subject_type: 'asset',
        object_type: ENTITY_OBJECT_TYPES.LOCATION,
        subject_id: tabId,
        object_id: null,
        value: Array.isArray(values[fieldId])
          ? values[fieldId].join(',')
          : values[fieldId]
      });
    }

    try {
      showLoader(true);
      await updateValues(payload);

      await deleteImages();
      await updateImages();
      setFormData({ ...values });
      setIsEditMode(false);

      setShowAlert(true);
      setAlertData({ type: 'success', message: 'Successfully saved!' });
    } catch (err) {
      setShowAlert(true);
      setAlertData({
        type: 'error',
        message: 'Failed to save. Please, try again later.'
      });
    } finally {
      showLoader(false);
    }

    if (reloadImagesRef.current) {
      reloadImagesRef.current();
    }
    resetPendingImages();
  };

  const formik = useFormik({
    initialValues: { ...formData },
    enableReinitialize: true,
    validate: (values) => ({
      ...validatePercentageFields(fieldsData, values),
      ...validateTimeFields(fieldsData, values)
    }),
    onSubmit: saveChanges
  });

  const selectCallback = (key, id) => {
    setSelectedId((prev) => ({ ...prev, [key]: !!id }));
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  const closeAlert = () => {
    setShowAlert(false);
    setAlertData(defaultAlertData);
  };

  const iconButtonClasses = ['edit-button', isEditMode ? 'active' : '']
    .filter(Boolean)
    .join(' ');

  // Location Tab has different data layout
  // so to code readability and reduce overengineering here is not using <TabDetails />
  return (
    <>
      <IconButton
        aria-label="Edit details"
        className={iconButtonClasses}
        type="button"
        disabled={false}
        onClick={toggleEditMode}
      >
        <EditIcon />
      </IconButton>
      <form
        onSubmit={formik.handleSubmit}
        onReset={(event) => {
          discardChanges();
          formik.handleReset(event);
        }}
      >
        <div className="image-row">
          <h4>Image</h4>
          <AssetImageCarousel
            addressCoords={addressCoords}
            assetId={tabId}
            editable={isEditMode}
            onImageChange={isEditMode ? handleImageChange : null}
            pendingImagesToAdd={imagesToAdd}
            pendingImagesToDelete={imagesToDelete}
            onRefreshComplete={storeReloadFunction}
          />
        </div>

        {fieldsData.map((card, idx) => {
          const key = `${card.title}-${idx}`;
          return (
            <FullDetailsAccordion
              key={key}
              id={key}
              detailsComponent={
                <DataView
                  orientation="horizontal"
                  card={card}
                  isEdit={isEditMode}
                  values={formik.values}
                  handleChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                />
              }
              selectCallback={(id) => selectCallback(key, id)}
              expanded={!!selectedId[key]}
            >
              <span className="title">{card.title}</span>
            </FullDetailsAccordion>
          );
        })}

        {isEditMode ? (
          <div className="form-actions">
            <Button type="reset">Cancel</Button>
            <Button type="submit">Save</Button>
          </div>
        ) : null}
      </form>

      <Snackbar
        open={showAlert}
        autoHideDuration={AUTOCLOSE_ALERT_DELAY}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={closeAlert}
      >
        <Alert severity={alertData.type === 'success' ? 'success' : 'error'}>
          {alertData.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LocationTab;
